// chartPalette — ordered CSS-var color palette for shared chart primitives.
//
// Uses only CSS custom property names defined in
// apps/web/src/styles/colors_and_type.css for BOTH light and dark themes.
// NO hardcoded hex/rgb/oklch values.
//
// WHY this ordering: accent-1 (brass/ochre) is the primary brand accent;
// the status foreground tokens provide visually distinct, colorblind-considerate
// hues (blue, green, yellow, red, neutral). Mirrors the precedent in
// apps/web/src/features/cargo/components/charts/chartColors.ts.

/** Ordered array of CSS-var color strings drawn from existing status/accent tokens. */
export const chartPalette: readonly string[] = [
  'var(--accent-1)',
  'var(--status-info-fg)',
  'var(--status-success-fg)',
  'var(--status-warn-fg)',
  'var(--status-error-fg)',
  'var(--status-neutral-fg)',
];

/**
 * Deterministic key → color mapper. Stable across renders for the same key;
 * cycles through `chartPalette` when keys exceed palette length.
 *
 * @aiInvariant uses djb2 string hash to index into the palette — the same key
 *   always maps to the same palette slot, independent of what other keys exist.
 *   Safe for concurrent use; no mutable state.
 */
export function keyColor(key: string): string {
  // WHY djb2: simple, fast, low collision rate for short category/key strings.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    // | 0 keeps the accumulator in 32-bit signed integer range on each step,
    // matching standard djb2 overflow behaviour in JavaScript.
    hash = ((hash << 5) + hash + key.charCodeAt(i)) | 0;
  }
  // chartPalette is a non-empty constant (6 entries); result is always defined.
  return chartPalette[Math.abs(hash) % chartPalette.length] as string;
}
